import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  apiPost,
  castErrorResult,
  companyIdSelector,
  DrawerType,
  errors,
  getBulkActivateShiftTemplatePath,
  getBulkRecalculateWalkingDistancePath,
  getBulkSetPassengersNearByStationPath,
  getPassengerBulkRidesParticipationPreviewPath,
  getPassengerBulkSendMessagePath,
  IApiError,
  IBulkSetPassengersNearByPayload,
  IPassengersRidesParticipationPreview,
  IPassengersSendMessage,
  IPassengersSendMessageResult,
  IRydeProcess,
  Log,
  normalizeErrors,
  RydeProcessTitle,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  toForbiddenTranslatableItem,
  translateInfoItem,
  useDrawers,
} from "@ryde/shared";

export const usePassengersBulkActions = () => {
  const setApiFetching = useSetRecoilState(apiDataFetchingAtom);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { openDrawer } = useDrawers();
  const { t } = useTranslation();

  const handleBulkFindNearbyStation = useEvent(
    async (
      payload: IBulkSetPassengersNearByPayload,
      avoidResetSelectedIds?: boolean,
    ): Promise<SimpleResult<unknown>> => {
      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getBulkSetPassengersNearByStationPath(
          companyId || 0,
        );
        const response = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: { payload },
        });

        if (!response) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!response?.success) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(response);
        }

        const resultData = response.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkSetPassengersNearBy,
            entity: "passenger",
            avoidResetSelectedIds,
          },
        });
        return response;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.nearbyAssignmentStationFailed"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(
          t("errors.nearbyAssignmentStationFailed"),
          normalizedError,
        );
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setApiFetching(false);
      }
    },
  );

  const handleBulkRecalculateWalkingDistance = useEvent(
    async (ids: number[]): Promise<SimpleResult<unknown>> => {
      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getBulkRecalculateWalkingDistancePath(
          companyId || 0,
        );
        const response = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: { payload: { ids } },
        });

        if (!response) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!response?.success) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(response);
        }

        const resultData = response.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRecalculateWalkingDistance,
            entity: "passenger",
          },
        });
        return response;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.recalculateWalkingDistanceFailed"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(
          t("errors.recalculateWalkingDistanceFailed"),
          normalizedError,
        );
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setApiFetching(false);
      }
    },
  );

  /**
   * Read-only preview of who would actually receive the message under the rides participation filter.
   * Runs on every (debounced) keystroke, so it stays out of the global fetching/error atoms and never
   * toasts — returning `undefined` lets the caller keep showing the unfiltered selection.
   */
  const fetchRidesParticipationRecipientIds = useEvent(
    async (payload: {
      ids: number[];
      lastRideWithinDays?: number | null;
    }): Promise<number[] | undefined> => {
      if (!companyId) {
        Log.warn(
          "fetchRidesParticipationRecipientIds aborted: companyId is not loaded yet",
        );
        return undefined;
      }

      try {
        const requestUrl =
          getPassengerBulkRidesParticipationPreviewPath(companyId);
        const response = await apiPost<
          SimpleResult<IPassengersRidesParticipationPreview> | undefined
        >(requestUrl, {
          body: {
            ids: payload.ids,
            ...(payload.lastRideWithinDays
              ? { lastRideWithinDays: payload.lastRideWithinDays }
              : {}),
          },
        });

        if (!response?.success) {
          return undefined;
        }

        return response.data?.passengerIds ?? [];
      } catch (error) {
        // Preview only: a failure must never block the send, which filters server-side anyway.
        Log.debug("Failed to preview rides participation recipients", error);
        return undefined;
      }
    },
  );

  const bulkSendMessageToPassengers = useEvent(
    async (payload: Partial<IPassengersSendMessage>): Promise<boolean> => {
      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getPassengerBulkSendMessagePath(companyId || 0);
        const response = await apiPost<
          SimpleResult<IPassengersSendMessageResult> | undefined
        >(requestUrl, {
          body: payload,
        });

        if (!response?.success) {
          // Surface the server's reason — e.g. the rides participation filter matched nobody.
          Log.errorAndToast(
            t("errors.failedToSendMessagesToPassengers"),
            response?.error,
          );
          return false;
        }

        const { sentCount, filteredOutCount } = response.data ?? {};

        if (filteredOutCount) {
          Log.infoAndToast(
            t("ride.bulk.messagesSentToPassengersFiltered", {
              sentCount,
              selectedCount: (sentCount ?? 0) + filteredOutCount,
              filteredOutCount,
            }),
          );
          return true;
        }

        Log.infoAndToast(t("ride.bulk.messagesSentToPassengers"));
        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToSendMessagesToPassengers"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(t("errors.failedToSendMessagesToPassengers"), error);
        setErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setApiFetching(false);
      }
    },
  );

  const bulkActivateShiftTemplate = useEvent(
    async (payload: {
      ids: number[];
      startDate: TDateISO;
    }): Promise<SimpleResult<unknown>> => {
      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getBulkActivateShiftTemplatePath(companyId || 0);
        const response = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: { ids: payload.ids, payload: { startDate: payload.startDate } },
        });

        if (!response) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!response?.success) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(response);
        }

        const resultData = response.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkActivateShiftTemplate,
            entity: "passenger",
          },
        });
        return response;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.bulkActivateShiftTemplateFailed"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(
          t("errors.bulkActivateShiftTemplateFailed"),
          normalizedError,
        );
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setApiFetching(false);
      }
    },
  );

  return {
    handleBulkFindNearbyStation,
    handleBulkRecalculateWalkingDistance,
    bulkSendMessageToPassengers,
    fetchRidesParticipationRecipientIds,
    bulkActivateShiftTemplate,
  };
};
